import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, History, PenLine, Save, Send, Trash2, Users } from "lucide-react";
import { fdate, LegalHtml } from "@/components/legal/LegalTabs";
import { LegalHistory } from "./LegalHistory";

const SUB = [["edit", "Éditer", PenLine], ["preview", "Aperçu", Eye], ["history", "Historique", History]];

export function LegalEditor({ doc, placeholders, onSaved }) {
  const [sub, setSub] = useState("edit");
  const [title, setTitle] = useState(doc.draft_title || doc.title);
  const [md, setMd] = useState(doc.draft_md ?? doc.content_md);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [pub, setPub] = useState(false);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(doc.key !== "mentions_legales");
  const dirty = md !== (doc.draft_md ?? doc.content_md) || title !== (doc.draft_title || doc.title);

  useEffect(() => {
    if (sub !== "preview") return;
    api.post("/admin/legal/preview", { content_md: md }).then((r) => setHtml(r.data.html)).catch(() => setHtml("<p>Aperçu indisponible</p>"));
  }, [sub, md]);

  const save = async (publish) => {
    setBusy(true);
    try {
      await api.put(`/admin/legal/${doc.key}`, { title, content_md: md, publish, change_note: publish ? note : undefined, notify: publish && notify });
      toast.success(publish ? `Version ${doc.version + 1} publiée` : "Brouillon enregistré");
      setPub(false); setNote("");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const discard = async () => {
    if (!window.confirm("Abandonner le brouillon et revenir à la version publiée ?")) return;
    try { await api.delete(`/admin/legal/${doc.key}/draft`); setMd(doc.content_md); setTitle(doc.title); toast.success("Brouillon supprimé"); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const insert = (k) => setMd((m) => `${m}{{${k}}}`);

  return (
    <div className="space-y-4" data-testid={`legal-editor-${doc.key}`}>
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#495057]">
        <span className="font-mono font-bold uppercase tracking-wider">v{doc.version} en vigueur</span>
        <span>· publiée le {fdate(doc.published_at)}</span>
        {doc.acceptance_roles?.length > 0 && <span className="inline-flex items-center gap-1" data-testid="legal-acceptances"><Users className="h-3.5 w-3.5" />{doc.acceptances_current} entreprise(s) ont accepté cette version</span>}
        {doc.has_draft && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F59E0B] text-white px-2 py-0.5" data-testid="legal-draft-badge">Brouillon en attente</span>}
        <span className="flex-1" />
        <div className="flex border border-[#DEE2E6]">
          {SUB.map(([k, l, Icon]) => <button type="button" key={k} onClick={() => setSub(k)} data-testid={`legal-sub-${k}`} className={`px-3 h-8 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 ${sub === k ? "bg-[#0A0A0A] text-white" : "bg-white text-[#495057]"}`}><Icon className="h-3.5 w-3.5" />{l}</button>)}
        </div>
      </div>

      {sub === "history" ? <LegalHistory docKey={doc.key} currentVersion={doc.version} onRestored={onSaved} /> : (
        <div className="grid lg:grid-cols-[1fr_260px] gap-6">
          <div className="bg-white border border-[#DEE2E6] p-5 space-y-4">
            {sub === "edit" ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Titre</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="legal-title" className="h-10 mt-1 font-display font-bold" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Contenu (Markdown)</Label>
                  <Textarea value={md} onChange={(e) => setMd(e.target.value)} rows={28} data-testid="legal-content" className="mt-1 font-mono text-xs leading-relaxed" />
                  <div className="text-[10px] text-[#868E96] mt-1">{md.length.toLocaleString("fr-FR")} caractères · titres <code>#</code> / <code>##</code>, listes <code>-</code>, gras <code>**texte**</code>, liens <code>[texte](url)</code>, tableaux <code>|</code>.</div>
                </div>
              </>
            ) : <div className="p-2" data-testid="legal-preview"><LegalHtml html={html} testid="legal-preview-html" /></div>}
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-[#DEE2E6] p-4 space-y-2">
              <Button onClick={() => save(false)} disabled={busy || !dirty} data-testid="legal-save-draft" variant="outline" className="w-full h-10"><Save className="h-4 w-4 mr-2" />Enregistrer le brouillon</Button>
              <Button onClick={() => setPub(true)} disabled={busy || !md.trim()} data-testid="legal-publish-open" className="w-full h-10 bg-[#002FA7] text-white"><Send className="h-4 w-4 mr-2" />Publier (v{doc.version + 1})</Button>
              {doc.has_draft && <Button onClick={discard} variant="outline" data-testid="legal-discard-draft" className="w-full h-9 text-xs border-[#E60000] text-[#E60000]"><Trash2 className="h-3.5 w-3.5 mr-1" />Abandonner le brouillon</Button>}
              <p className="text-[10px] text-[#495057] pt-1">Le brouillon n'est visible que par l'équipe PAG. La publication remplace la version en ligne et crée une entrée d'historique.</p>
            </div>
            <div className="bg-white border border-[#DEE2E6] p-4" data-testid="legal-placeholders">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#868E96] mb-2">Balises disponibles</div>
              <div className="flex flex-wrap gap-1">
                {(placeholders || []).map((p) => <button type="button" key={p.key} onClick={() => insert(p.key)} title={p.label} data-testid={`legal-ph-${p.key}`} className="font-mono text-[10px] px-1.5 py-0.5 bg-[#F1F3F5] hover:bg-[#0A0A0A] hover:text-white">{`{{${p.key}}}`}</button>)}
              </div>
              <p className="text-[10px] text-[#495057] mt-2">Remplies depuis « Identité de l'éditeur » à l'affichage.</p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={pub} onOpenChange={setPub}>
        <DialogContent className="max-w-lg" data-testid="legal-publish-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Publier la version {doc.version + 1}</DialogTitle>
            <DialogDescription>Le document sera immédiatement visible sur le site public et dans les espaces gérant / salarié.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Note de version (facultatif)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : précision sur la facturation des options" data-testid="legal-change-note" className="h-10 mt-1" />
            </div>
            {doc.key !== "mentions_legales" && (
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="legal-notify" className="mt-0.5" />
                <span>Prévenir tous les gérants (email + notification) et leur demander d'accepter la nouvelle version.</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPub(false)}>Annuler</Button>
            <Button onClick={() => save(true)} disabled={busy} data-testid="legal-publish-confirm" className="bg-[#002FA7] text-white">{busy ? "Publication…" : "Publier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
